import React from "react";
import FieldMappingUnmappedTargetsForCommonModel from "./FieldMappingUnmappedTargetsForCommonModel";
import {
  FieldMappingCommonModelConfiguration,
  LinkedAccount,
} from "../../../../../../models/Entities";
import useFieldmappingTargetsPage from "./hooks/useFieldMappingTargetsPage";
import { SectionHeaderWrapper } from "../../../../../shared/SectionHeaderWrapper";
import {
  Button,
  ButtonVariant,
  Card,
  Link,
  Typeahead,
  SkeletonLoader,
} from "@merge-api/merge-javascript-shared";
import { ArrowLeft } from "lucide-react";
import {
  DOCS_FIELD_MAPPINGS_PATH,
  navigateToLinkedAccountFieldMappings,
} from "../../../../../../router/RouterUtils";
import { useHistory } from "react-router-dom";
import useAppContext from "../../../../../context/useAppContext";

type Props = {
  linkedAccount: LinkedAccount;
};
const FieldMappingUnmappedTargetsPage = ({ linkedAccount }: Props) => {
  const history = useHistory();
  const params = new URLSearchParams(window.location.search);
  const preSelectedCommonModel = params.get("commonModelName") ?? null;

  const {
    fieldMappingInformation,
    fieldMappingConfigurations,
    availableFieldMappings,
    selectedCommonModels,
    setSelectedCommonModels,
  } = useFieldmappingTargetsPage({
    linkedAccount: linkedAccount,
    preSelectedCommonModel: preSelectedCommonModel,
  });

  const filterFieldMappingConfigs = () => {
    if (selectedCommonModels.length == 0) {
      return fieldMappingConfigurations;
    }

    return fieldMappingConfigurations!.filter((config: FieldMappingCommonModelConfiguration) => {
      return selectedCommonModels.includes(config.common_model_name);
    });
  };

  const { user } = useAppContext();
  const isJmesPathDeprecationEnabled = user.is_jmes_path_deprecation_dashboard_enabled ?? false;

  return (
    <div className="flex flex-col gap-y-4">
      <Button
        variant={ButtonVariant.TextBlue}
        leftIcon={<ArrowLeft size={12} />}
        onClick={() => navigateToLinkedAccountFieldMappings(history, linkedAccount.id)}
      >
        Field Mapping
      </Button>
      <SectionHeaderWrapper
        title={`Unmapped ${linkedAccount.organization.name}-wide target fields`}
        subtitle={
          <>
            Connect predefined target fields to new {linkedAccount.integration.name} Remote Fields.
            Any changes made here are automatically saved and will be updated in the next sync. Go
            to{" "}
            <Link href="/configuration/field-mappings/target-fields" target="_blank">
              Advanced Configuration
            </Link>{" "}
            to add more organization-wide target fields or set default integration-wide mappings.{" "}
            <Link href={DOCS_FIELD_MAPPINGS_PATH} target="_blank">
              Learn more
            </Link>
            .
          </>
        }
      >
        <div className="my-6">
          {linkedAccount &&
          fieldMappingInformation &&
          availableFieldMappings &&
          fieldMappingConfigurations ? (
            <>
              <Typeahead
                disableCloseOnSelect
                multiple
                showPlusFormat
                onChange={(event: any, selected: string[] | null) => {
                  setSelectedCommonModels(selected ?? []);
                }}
                options={fieldMappingConfigurations.map(
                  (config: FieldMappingCommonModelConfiguration) => {
                    return config.common_model_name;
                  },
                )}
                tagsLimit={3}
              />
              <div className="flex flex-col gap-6 mt-6">
                {filterFieldMappingConfigs()!.map(
                  (configuration: FieldMappingCommonModelConfiguration) => {
                    return (
                      <FieldMappingUnmappedTargetsForCommonModel
                        commonModel={configuration.common_model_name}
                        fieldMappingTargets={fieldMappingInformation.field_mapping_targets.filter(
                          (target: { common_model_name: string }) => {
                            return target.common_model_name == configuration.common_model_name;
                          },
                        )}
                        isRemoteDataEnabled={configuration.has_remote_data_enabled}
                        fieldMappingOptions={availableFieldMappings}
                        linkedAccount={linkedAccount}
                        isJmesPathDeprecationEnabled={isJmesPathDeprecationEnabled}
                      />
                    );
                  },
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-6">
              <SkeletonLoader height={40} fullWidth borderRadius={8} />
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <div className="px-6 py-5 border-b border-gray-10">
                    <SkeletonLoader height={24} width={120} borderRadius={8} />
                  </div>
                  <div className="py-5 px-6 flex flex-col gap-6">
                    <SkeletonLoader height={20} fullWidth borderRadius={8} />
                    <SkeletonLoader height={56} fullWidth borderRadius={8} />
                    <SkeletonLoader height={56} fullWidth borderRadius={8} />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </SectionHeaderWrapper>
    </div>
  );
};

export default FieldMappingUnmappedTargetsPage;
